import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

const SUGGEST_FIELDS = 'title,body,product_type,tag,variants.title,vendor';
const RELATED_SECTION_ID = 'search-related-suggest';
const RELATED_RESOLVED_EVENT = 'search-related-resolved';
const PREFETCH_KEY = '__searchRelatedSuggestPrefetch';

/**
 * @typedef {{ query: string, promise: Promise<string> }} RelatedSuggestPrefetch
 */

/**
 * Gap-fills pages/articles from Suggest.
 * Collections only when native search has no products (Suggest-only path) —
 * otherwise collection products already appear in the product grid.
 * Native SSR hits stay visible; Suggest only appends missing items.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} results
 *
 * @extends {Component<Refs>}
 */
class SearchRelatedSuggest extends Component {
  requiredRefs = ['results'];

  /** @type {AbortController | null} */
  #abortController = null;

  get #includeCollections() {
    return this.hasAttribute('data-include-collections');
  }

  connectedCallback() {
    super.connectedCallback();
    void this.#loadRelatedResources();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Prefer header inline prefetch (starts before this module loads).
   * @param {string} query
   * @returns {Promise<string> | null}
   */
  #takePrefetch(query) {
    const prefetch = /** @type {RelatedSuggestPrefetch | undefined} */ (window[PREFETCH_KEY]);
    if (!prefetch || prefetch.query !== query || !(prefetch.promise instanceof Promise)) {
      return null;
    }

    delete window[PREFETCH_KEY];
    return prefetch.promise;
  }

  async #loadRelatedResources() {
    const query = this.dataset.query?.trim();
    if (!query) {
      this.#markResolved();
      return;
    }

    const resourceTypes = this.#includeCollections ? 'collection,page,article' : 'page,article';
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', resourceTypes);
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', SUGGEST_FIELDS);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const prefetched = this.#takePrefetch(query);
      const resultsMarkup = prefetched
        ? await prefetched
        : await sectionRenderer.getSectionHTML(RELATED_SECTION_ID, false, url);
      if (signal.aborted) return;

      if (!resultsMarkup) {
        this.#markResolved();
        return;
      }

      const doc = new DOMParser().parseFromString(resultsMarkup, 'text/html');
      const content = doc.querySelector('[data-search-related-suggest]');
      if (!(content instanceof HTMLElement) || content.dataset.empty === 'true') {
        this.#markResolved();
        return;
      }

      // No scroll compensation — revealing/updating the header above must not push the page down.
      this.#mergeRelatedMarkup(content);
      this.#updateHeaderRelatedCounts();
      this.#markResolved();
    } catch (error) {
      if (signal.aborted) return;
      this.#markResolved();
      throw error;
    }
  }

  /**
   * Keep SSR items; append Suggest-only resources (matched by pathname).
   * @param {HTMLElement} content
   */
  #mergeRelatedMarkup(content) {
    const incomingRoot = content.querySelector('.search-other-resources');
    if (!(incomingRoot instanceof HTMLElement)) return;

    const existingRoot = this.refs.results.querySelector('.search-other-resources');
    if (!(existingRoot instanceof HTMLElement)) {
      this.refs.results.replaceChildren(...content.cloneNode(true).childNodes);
      return;
    }

    const list = existingRoot.querySelector('.search-other-resources__list');
    const incomingList = incomingRoot.querySelector('.search-other-resources__list');
    if (!(list instanceof HTMLElement) || !(incomingList instanceof HTMLElement)) {
      this.refs.results.replaceChildren(...content.cloneNode(true).childNodes);
      return;
    }

    const seen = new Set([...list.querySelectorAll('a[href]')].map((anchor) => this.#resourceKey(anchor.href)));
    const includeCollections = this.#includeCollections;

    for (const item of incomingList.querySelectorAll('[data-resource-type]')) {
      if (!(item instanceof HTMLElement)) continue;
      if (!includeCollections && item.dataset.resourceType === 'collection') continue;

      const href = item.querySelector('a[href]')?.href;
      if (!href) continue;

      const key = this.#resourceKey(href);
      if (seen.has(key)) continue;

      list.appendChild(item.cloneNode(true));
      seen.add(key);
    }
  }

  /**
   * @param {string} href
   */
  #resourceKey(href) {
    try {
      return new URL(href, location.origin).pathname;
    } catch {
      return href;
    }
  }

  #markResolved() {
    this.dataset.relatedResolved = 'true';
    this.dispatchEvent(new Event(RELATED_RESOLVED_EVENT, { bubbles: true }));
  }

  #showHeaderResults() {
    const results = document.querySelector('[data-search-header-results]');
    const empty = document.querySelector('[data-search-header-empty]');
    if (results instanceof HTMLElement) results.hidden = false;
    if (empty instanceof HTMLElement) empty.hidden = true;
  }

  /**
   * Sync header related count to the list actually shown.
   * Non-product hits are summed as a single "pages" count.
   */
  #updateHeaderRelatedCounts() {
    const countEl = document.querySelector('[data-search-header-related-count]');
    if (!(countEl instanceof HTMLElement)) return;

    const relatedCount = this.refs.results.querySelectorAll('[data-resource-type]').length;
    const label = this.#formatCountLabel(relatedCount, this.dataset.pagesCountOne, this.dataset.pagesCountOther);

    if (!label) {
      countEl.textContent = '';
      countEl.hidden = true;
      return;
    }

    countEl.textContent = label;
    countEl.hidden = false;
    this.#showHeaderResults();
  }

  /**
   * @param {number} count
   * @param {string | undefined} one
   * @param {string | undefined} other
   */
  #formatCountLabel(count, one, other) {
    if (count <= 0) return '';

    const template = count === 1 ? one : other;
    if (!template) return '';

    return template.replace(/\d+/, String(count));
  }
}

if (!customElements.get('search-related-suggest')) {
  customElements.define('search-related-suggest', SearchRelatedSuggest);
}
